/**
 * Chat Store - Zustand store for AI Chat state management
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  ChatMessage,
  ChatState,
  Approval,
  PageContext,
  ToolCall,
  ToolResult,
} from '../types/chat';
import * as mcpService from '../services/mcpService';

interface ChatStore extends ChatState {
  // Actions
  toggle: () => void;
  open: () => void;
  close: () => void;
  minimize: () => void;
  maximize: () => void;

  // Message Actions
  sendMessage: (content: string) => Promise<void>;
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
  clearMessages: () => void;

  // Conversation Actions
  startNewConversation: () => Promise<void>;
  loadConversation: (conversationId: string) => Promise<void>;
  resumeLastConversation: () => Promise<void>;

  // Approval Actions
  loadApprovals: () => Promise<void>;
  approveAction: (approvalId: string) => Promise<void>;
  rejectAction: (approvalId: string, reason?: string) => Promise<void>;

  // Context Actions
  setContext: (context: PageContext) => void;

  // Error handling
  setError: (error: string | null) => void;
  clearError: () => void;
}

export const useChatStore = create<ChatStore>()(
  persist(
    (set, get) => ({
      // Initial State
      isOpen: false,
      isMinimized: false,
      isStreaming: false,
      error: null,
      conversationId: null,
      messages: [],
      pendingApprovals: [],
      currentContext: null,

      // UI Actions
      toggle: () => set((state) => ({ isOpen: !state.isOpen, isMinimized: false })),
      open: () => set({ isOpen: true, isMinimized: false }),
      close: () => set({ isOpen: false }),
      minimize: () => set({ isMinimized: true }),
      maximize: () => set({ isMinimized: false }),

      // Message Actions
      sendMessage: async (content: string) => {
        const { conversationId, addMessage, setError } = get();

        // Add user message immediately
        addMessage({
          conversationId: conversationId || 'temp',
          role: 'user',
          content,
        });

        set({ isStreaming: true, error: null });

        try {
          const response = await mcpService.sendChatMessage(content, conversationId || undefined);

          // Update conversation ID if new
          if (!conversationId) {
            set({ conversationId: response.conversationId });
          }

          // Add assistant response
          addMessage({
            conversationId: response.conversationId,
            role: 'assistant',
            content: response.response.content,
            toolCalls: response.response.toolCalls,
            toolResults: response.response.toolResults,
          });

          // Check for pending approvals in tool results
          const approvalResults = response.response.toolResults?.filter(
            (r) => {
              try {
                const content = typeof r.content === 'string' ? JSON.parse(r.content) : r.content;
                return content?.pendingApproval === true;
              } catch {
                return false;
              }
            }
          );

          if (approvalResults && approvalResults.length > 0) {
            // Reload approvals
            get().loadApprovals();
          }
        } catch (error) {
          setError(error instanceof Error ? error.message : 'Failed to send message');
        } finally {
          set({ isStreaming: false });
        }
      },

      addMessage: (message) => {
        const newMessage: ChatMessage = {
          ...message,
          id: crypto.randomUUID(),
          timestamp: new Date(),
        };

        set((state) => ({
          messages: [...state.messages, newMessage],
        }));
      },

      clearMessages: () => set({ messages: [], conversationId: null }),

      // Conversation Actions
      startNewConversation: async () => {
        // Just clear state - a new conversation will be created when user sends first message
        set({ messages: [], conversationId: null, error: null });
      },

      loadConversation: async (conversationId: string) => {
        set({ isStreaming: true, error: null });

        try {
          const messages = await mcpService.getConversationMessages(conversationId);
          set({
            conversationId,
            messages: messages.map((m) => ({
              ...m,
              timestamp: new Date(m.timestamp),
            })),
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to load conversation',
          });
        } finally {
          set({ isStreaming: false });
        }
      },

      resumeLastConversation: async () => {
        const { conversationId, messages, loadConversation } = get();
        // Only resume if we have a saved conversationId but no messages loaded yet
        if (conversationId && messages.length === 0) {
          try {
            await loadConversation(conversationId);
          } catch (error) {
            // If conversation can't be loaded (deleted, etc.), clear it
            console.warn('Could not resume conversation:', error);
            set({ conversationId: null });
          }
        }
      },

      // Approval Actions
      loadApprovals: async () => {
        try {
          const approvals = await mcpService.getPendingApprovals();
          set({
            pendingApprovals: approvals.map((a) => ({
              ...a,
              expiresAt: new Date(a.expiresAt),
              createdAt: new Date(a.createdAt),
              reviewedAt: a.reviewedAt ? new Date(a.reviewedAt) : null,
            })),
          });
        } catch (error) {
          console.error('Failed to load approvals:', error);
        }
      },

      approveAction: async (approvalId: string) => {
        set({ isStreaming: true, error: null });

        try {
          const result = await mcpService.approveAction(approvalId);

          if (result.success) {
            // Remove from pending and add success message
            set((state) => ({
              pendingApprovals: state.pendingApprovals.filter((a) => a.id !== approvalId),
            }));

            get().addMessage({
              conversationId: get().conversationId || 'temp',
              role: 'system',
              content: `Action approved and executed successfully.${result.result ? ` Result: ${JSON.stringify(result.result)}` : ''}`,
            });
          } else {
            set({ error: result.error || 'Approval failed' });
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to approve action',
          });
        } finally {
          set({ isStreaming: false });
        }
      },

      rejectAction: async (approvalId: string, reason?: string) => {
        set({ isStreaming: true, error: null });

        try {
          const result = await mcpService.rejectAction(approvalId, reason);

          if (result.success) {
            // Remove from pending and add rejection message
            set((state) => ({
              pendingApprovals: state.pendingApprovals.filter((a) => a.id !== approvalId),
            }));

            get().addMessage({
              conversationId: get().conversationId || 'temp',
              role: 'system',
              content: `Action rejected.${reason ? ` Reason: ${reason}` : ''}`,
            });
          } else {
            set({ error: result.error || 'Rejection failed' });
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to reject action',
          });
        } finally {
          set({ isStreaming: false });
        }
      },

      // Context Actions
      setContext: (context: PageContext) => set({ currentContext: context }),

      // Error handling
      setError: (error: string | null) => set({ error }),
      clearError: () => set({ error: null }),
    }),
    {
      name: 'simcheck-chat-store',
      partialize: (state) => ({
        // Persist UI state and conversation ID (messages loaded on demand)
        isOpen: state.isOpen,
        isMinimized: state.isMinimized,
        conversationId: state.conversationId,
      }),
    }
  )
);

// Export hook for using chat store
export const useChat = () => useChatStore();
